import type { ColumnDef } from '@tanstack/react-table';
import DriverIconLinks from '../../components/DriverIconLinks';
import { Tooltip } from '../../components/ui/Tooltip';
import type { DriverStatusBoardRow } from '../../types/driverStatusBoard';

// V1 dashboard_dispatch.pl %abbr
const MOVE_ABBR: Record<string, string> = {
  IMPORT: 'IM', EXPORT: 'EX', INBOUND: 'IN', OUTBOUND: 'OUT', DOMESTIC: 'DO', REPO: 'RE',
};

export function legStatusBadgeClass(status: string): string {
  const s = (status || '').toUpperCase().trim();
  if (s === 'DEST DEPARTED') return 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300';
  if (['OUTGATED', 'ORIG ARRIVED', 'ORIG DEPARTED', 'DEST ARRIVED'].includes(s)) {
    return 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300';
  }
  if (s === 'LATE' || s === 'NO GO') return 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300';
  return 'bg-gray-200 text-label dark:bg-gray-700 dark:text-gray-200';
}

function multiline(text: string) {
  return <div className="text-xs whitespace-pre-line">{text}</div>;
}

// Columns in V1 "[R] Driver Status" order; flat, read-only.
export function buildDriverStatusBoardColumns(
  openPro: (pronumber: number) => void,
): ColumnDef<DriverStatusBoardRow>[] {
  return [
    {
      accessorKey: 'driver', header: 'Driver', size: 90,
      cell: ({ row }) => {
        const { driver, muledriver } = row.original;
        if (!driver || driver === '000' || muledriver === 'Y') return <span>{driver}</span>;
        return (
          <span className="inline-flex items-center gap-1">
            {driver}
            <DriverIconLinks unitNumber={driver} showMoves={true} />
          </span>
        );
      },
    },
    {
      accessorKey: 'pronumber', header: 'Pro #', size: 100,
      cell: ({ row }) => (
        <button
          type="button"
          className="text-blue-600 dark:text-blue-400 hover:underline"
          onClick={(e) => { e.stopPropagation(); openPro(row.original.pronumber); }}
        >
          {row.original.pronumber}
        </button>
      ),
    },
    {
      accessorKey: 'movetype', header: 'Move Type', size: 80,
      cell: ({ getValue }) => {
        const v = String(getValue() ?? '');
        return MOVE_ABBR[v] ? <span title={v}>{MOVE_ABBR[v]}</span> : v;
      },
    },
    { accessorKey: 'dispatch_time', header: 'Dispatch Time', size: 120 },
    { accessorKey: 'dispatcher', header: 'Dispatcher', size: 110 },
    { accessorKey: 'legtype', header: 'Leg Type', size: 90 },
    {
      accessorKey: 'orig', header: 'Origin', size: 200,
      cell: ({ row }) => (
        <Tooltip content={multiline(row.original.originfo)} disabled={!row.original.originfo}>
          <span className="truncate block">{row.original.orig}</span>
        </Tooltip>
      ),
    },
    { accessorKey: 'legaction', header: 'Action', size: 100 },
    {
      accessorKey: 'dest', header: 'Destination', size: 200,
      cell: ({ row }) => (
        <Tooltip content={multiline(row.original.destinfo)} disabled={!row.original.destinfo}>
          <span className="truncate block">{row.original.dest}</span>
        </Tooltip>
      ),
    },
    { accessorKey: 'outgate', header: 'Outgate', size: 120 },
    { accessorKey: 'deadline', header: 'Deadline', size: 120 },
    { accessorKey: 'deadline_type', header: 'Deadline Type', size: 110 },
    {
      accessorKey: 'legstatus', header: 'Status', size: 130,
      cell: ({ getValue }) => {
        const v = String(getValue() ?? '');
        return (
          <span className={`inline-block rounded px-2 py-0.5 text-xs font-medium ${legStatusBadgeClass(v)}`}>
            {v || 'Dispatched'}
          </span>
        );
      },
    },
    {
      accessorKey: 'is_current', header: 'Current', size: 80,
      cell: ({ getValue }) => (getValue() === 'Y' ? (
        <span
          className="inline-flex items-center gap-1 text-xs font-medium text-blue-700 dark:text-blue-300"
          title="Driver's current move"
        >
          <span aria-hidden="true" className="inline-block h-2 w-2 rounded-full bg-blue-500" />
          Current
        </span>
      ) : null),
    },
    {
      accessorKey: 'our_eta', header: 'Our ETA', size: 90,
      cell: ({ row }) => {
        const { our_eta, our_eta_risk } = row.original;
        if (!our_eta) return null;
        return (
          <span className={`tabular-nums ${etaRiskClass(our_eta_risk)}`} title={etaRiskTitle(our_eta_risk)}>
            {our_eta}
          </span>
        );
      },
    },
    {
      accessorKey: 'samsara_eta', header: 'Samsara ETA', size: 130,
      cell: ({ row }) => {
        const { samsara_eta, samsara_status } = row.original;
        if (!samsara_eta) return null;
        const isTime = samsara_status === 'en route';
        return (
          <span
            className={isTime ? 'tabular-nums text-sky-700 dark:text-sky-300' : 'text-xs text-muted'}
            title={isTime ? 'Samsara route ETA to destination' : `Samsara: ${samsara_status}`}
          >
            {samsara_eta}
          </span>
        );
      },
    },
    { accessorKey: 'otr', header: 'OTR', size: 80 },
    { accessorKey: 'preference', header: 'Preference', size: 100 },
  ];
}

function etaRiskClass(risk: string): string {
  switch (risk) {
    case 'at_risk': return 'text-amber-700 dark:text-amber-300';
    case 'will_miss':
    case 'missed': return 'text-red-700 dark:text-red-300';
    default: return '';
  }
}

function etaRiskTitle(risk: string): string {
  switch (risk) {
    case 'at_risk': return 'Projected arrival is close to the deadline';
    case 'will_miss': return 'Projected arrival is after the deadline';
    case 'missed': return 'Deadline already passed';
    default: return 'FastCMS projected arrival at destination';
  }
}

/** Left-edge indicator for the driver's current move (TanTable getIndicatorInfo). */
export function currentMoveIndicator(row: DriverStatusBoardRow): { color: string; label: string | null } | null {
  return row.is_current === 'Y' ? { color: 'bg-blue-500', label: 'Current move' } : null;
}

// Row tint: legs with no container orange. V1 also shaded DEST DEPARTED rows
// gray; dropped on request — the Status column already carries it.
export function driverStatusRowClass(row: DriverStatusBoardRow): string {
  if (!row.container) return 'bg-orange-100 dark:bg-orange-900/30';
  return '';
}
